import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router";
import { useAuth } from "@/lib/auth-context";
import { signInWithEmail, signInWithGoogle } from "@/lib/firebase";
import { LogoIcon } from "@/components/layout/Logo";
import { GoogleIcon } from "@/components/icons/GoogleIcon";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type Status = "waiting" | "sending" | "success" | "error";

export function CLIAuthPage() {
  const { user, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const port = searchParams.get("port");
  const code = searchParams.get("code");

  const [status, setStatus] = useState<Status>("waiting");
  const [errorMsg, setErrorMsg] = useState("");
  const [fallbackToken, setFallbackToken] = useState("");
  const [copied, setCopied] = useState(false);
  const sentRef = useRef(false);

  // Login form state (shown when user is not authenticated)
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // When user is authenticated, send credentials to CLI
  useEffect(() => {
    if (!user || sentRef.current) return;
    if (!port || !code) return;

    sentRef.current = true;

    async function sendCredentials() {
      setStatus("sending");
      try {
        const idToken = await user!.getIdToken(true);
        const refreshToken =
          "refreshToken" in user! ? (user as { refreshToken: string }).refreshToken : "";
        const payload = {
          idToken,
          refreshToken,
          email: user!.email,
          apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
          code,
        };

        const res = await fetch(`http://localhost:${port}/callback`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (res.ok) {
          setStatus("success");
        } else {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || `HTTP ${res.status}`);
        }
      } catch (err) {
        setStatus("error");
        setErrorMsg(
          err instanceof Error ? err.message : "Failed to connect to CLI"
        );
        try {
          const token = await user!.getIdToken();
          setFallbackToken(token);
        } catch {
          // If we can't even get the token, nothing to show
        }
      }
    }

    sendCredentials();
  }, [user, port, code]);

  async function handleCopy() {
    await navigator.clipboard.writeText(fallbackToken);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function handleEmailLogin(e: React.FormEvent) {
    e.preventDefault();
    setLoginError("");
    setSubmitting(true);
    try {
      await signInWithEmail(email, password);
      // useAuth will update -> useEffect sends credentials
    } catch (err: unknown) {
      setLoginError(
        err instanceof Error ? err.message : "Failed to sign in"
      );
      setSubmitting(false);
    }
  }

  async function handleGoogleLogin() {
    setLoginError("");
    setSubmitting(true);
    try {
      await signInWithGoogle();
    } catch (err: unknown) {
      setLoginError(
        err instanceof Error ? err.message : "Failed to sign in with Google"
      );
      setSubmitting(false);
    }
  }

  // ── Missing params ──────────────────────────────────────────

  if (!port || !code) {
    return (
      <CenteredCard>
        <LogoIcon className="h-10 w-10 text-foreground" />
        <h1 className="text-xl font-semibold text-foreground">Invalid link</h1>
        <p className="text-sm text-muted-foreground text-center">
          This page is used by the n8nvibes CLI to authenticate.
          <br />
          Run <code className="rounded bg-muted px-1.5 py-0.5 text-xs">n8nvibes login</code> from
          your terminal.
        </p>
      </CenteredCard>
    );
  }

  // ── Loading auth state ──────────────────────────────────────

  if (authLoading) {
    return (
      <CenteredCard>
        <Spinner />
        <p className="text-sm text-muted-foreground">Loading...</p>
      </CenteredCard>
    );
  }

  // ── Success ─────────────────────────────────────────────────

  if (status === "success") {
    return (
      <CenteredCard>
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
          <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-xl font-semibold text-foreground">CLI Authenticated</h1>
        <p className="text-sm text-muted-foreground text-center">
          Signed in as <strong>{user?.email}</strong>.
          <br />
          You can close this tab and return to your terminal.
        </p>
      </CenteredCard>
    );
  }

  // ── Sending ─────────────────────────────────────────────────

  if (status === "sending") {
    return (
      <CenteredCard>
        <Spinner />
        <p className="text-sm text-muted-foreground">
          Authenticating CLI...
        </p>
      </CenteredCard>
    );
  }

  // ── Error with fallback token ───────────────────────────────

  if (status === "error" && fallbackToken) {
    return (
      <CenteredCard>
        <LogoIcon className="h-10 w-10 text-foreground" />
        <h1 className="text-xl font-semibold text-foreground">
          Couldn't reach CLI
        </h1>
        <p className="text-sm text-muted-foreground text-center">
          {errorMsg}
          <br />
          Copy the token below and paste it in your terminal:
        </p>
        <div className="w-full">
          <textarea
            readOnly
            value={fallbackToken}
            rows={3}
            className="w-full rounded-md border bg-muted p-2 font-mono text-xs break-all resize-none"
          />
          <Button className="mt-2 w-full" onClick={handleCopy}>
            {copied ? "Copied!" : "Copy token"}
          </Button>
        </div>
      </CenteredCard>
    );
  }

  // ── Not authenticated — show inline login ───────────────────

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center gap-6 pt-6">
            <div className="flex items-center gap-3">
              <LogoIcon className="h-10 w-10 text-foreground" />
              <h1 className="text-3xl font-bold tracking-tight text-foreground">
                n8nvibes
              </h1>
            </div>

            <p className="text-sm text-muted-foreground text-center">
              Sign in to authenticate the CLI
            </p>

            {loginError && (
              <div className="w-full rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {loginError}
              </div>
            )}

            <form onSubmit={handleEmailLogin} className="flex w-full flex-col gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                />
              </div>

              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? "Signing in..." : "Sign in"}
              </Button>
            </form>

            <div className="flex w-full items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs text-muted-foreground">or</span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <Button
              variant="outline"
              className="w-full"
              onClick={handleGoogleLogin}
              disabled={submitting}
            >
              <GoogleIcon className="h-4 w-4" />
              Continue with Google
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Fallback: waiting state (shouldn't normally reach here) ──

  return (
    <CenteredCard>
      <Spinner />
      <p className="text-sm text-muted-foreground">Preparing...</p>
    </CenteredCard>
  );
}

// ── Shared layout wrapper ─────────────────────────────────────

function CenteredCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <Card className="w-full max-w-md">
        <CardContent className="flex flex-col items-center gap-6 pt-6 pb-8">
          {children}
        </CardContent>
      </Card>
    </div>
  );
}

function Spinner() {
  return (
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground border-t-foreground" />
  );
}
